import {
  Workflow,
  getWorkflowNameByPhoneNumber,
} from '@api/queries/getWorkflowNameByPhoneNumber';
import { sanitizePhoneNumberTo10Digits } from '@lib/utils/formatPhone/sanitizePhoneNumberTo10Digits';
import TasksModule from '@modules/TasksModule';
import { tasksState } from '@modules/TasksModule/state';
import { isValidPhoneNumber } from '@pages/settings/phoneNumberUtils';

export const WORKFLOW_CACHE_TTL_MS = 15 * 60 * 1000; // 15 minutes

export async function getWorkflowNameByPhoneWithCache(
  phoneNumber: string,
  getWorkflowNameByPhoneNumber: ({
    phoneNumber,
  }: {
    phoneNumber: string;
  }) => Promise<Workflow | null>,
): Promise<Workflow | null> {
  try {
    const cacheEntry = tasksState.phoneNumberToWorkflow[phoneNumber];
    const now = Date.now();

    if (cacheEntry && now - cacheEntry.fetchedAt < WORKFLOW_CACHE_TTL_MS) {
      return cacheEntry.workflow;
    }

    const workflow = await getWorkflowNameByPhoneNumber({ phoneNumber });
    tasksState.phoneNumberToWorkflow[phoneNumber] = {
      workflow,
      fetchedAt: now,
    };
    return workflow;
  } catch (error) {
    console.error(
      'Error fetching workflow name by phone with cache:',
      error,
      phoneNumber,
    );
    throw error;
  }
}

export async function fetchWorkflowNameForTheCall({
  toPhoneNumber,
  taskId,
}: {
  toPhoneNumber: string;
  taskId: string;
}) {
  try {
    const task = TasksModule.getTask(taskId);
    if (!task || task?.attributes?.workflow?.name) {
      // workflow already exists on the task
      return;
    }

    if (!isValidPhoneNumber(toPhoneNumber)) {
      throw new Error(
        `Invalid phone number in fetchWorkflowNameForTheCall, phoneNumber ${toPhoneNumber}`,
      );
    }
    const phoneNumber = sanitizePhoneNumberTo10Digits(toPhoneNumber);
    const workflow = await getWorkflowNameByPhoneWithCache(
      phoneNumber,
      getWorkflowNameByPhoneNumber,
    );

    if (workflow?.name) {
      TasksModule.setTaskAttribute(task.id, 'workflow', {
        id: workflow.id,
        name: workflow.name,
      });
    }
  } catch (error) {
    console.error(
      'Error in fetchWorkflowNameForTheCall:',
      error,
      taskId,
      toPhoneNumber,
    );
  }
}
